// Relative path: src/components/Recommendations/DesktopRecTabBar.js

import React from 'react';

/**
 * Desktop RecPrototype TabBar Component
 * 
 * Features:
 * - Displays clinic options as mini banners in a horizontal layout
 * - Shows ranking bubbles (gold, silver, bronze) for top options
 * - Includes animated selection indicator
 * - Navigation buttons positioned adjacent to mini banners
 * - Responsive buttons that become circular chevrons on smaller screens
 * 
 * @param {Object[]} options - Array of clinic options to display
 * @param {number} activeIndex - Index of the currently selected option
 * @param {function} onTabClick - Function to call when a tab is clicked
 */
const DesktopRecTabBar = ({
  options = [],
  activeIndex = 0,
  onTabClick = () => {},
}) => {
  /* Skip rendering if no options are available */
  if (options.length === 0) return null;

  /* Get ranking bubble background color based on index */
  const getRankingColor = (index) => {
    switch (index) {
      case 0: // First place - Gold
        return 'bg-gradient-to-br from-yellow-300 to-yellow-600';
      case 1: // Second place - Silver
        return 'bg-gradient-to-br from-gray-200 to-gray-400';
      case 2: // Third place - Bronze
        return 'bg-gradient-to-br from-yellow-600 to-yellow-800';
      default: // Other rankings
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
    }
  };

  /* Navigation handlers */
  const handlePrevious = () => {
    if (activeIndex > 0) {
      onTabClick(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < options.length - 1) {
      onTabClick(activeIndex + 1);
    }
  };

  /* MiniBanner component for desktop TabBar */
  const MiniBanner = ({ option = {}, isActive = false, index }) => {
    const {
      imageUrl = '',
      title = 'Option Title',
    } = option;

    const rankingColor = getRankingColor(index);

    return (
      <div className="relative">

        
    <button
      onClick={() => onTabClick(index)}
      disabled={index === activeIndex}
      className={`flex items-center shadow-lg rounded-lg bg-white py-2 m-1 px-2 transition-all duration-300 w-64 relative z-10
        ${isActive ? 'border-2 border-mm_blue scale-105' : 'border-2 border-gray-300 hover:shadow-xl hover:scale-105 hover:border-blue-300'}`}
    >

          {/* thumbnail */}
          <div className="w-14 h-14 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
            {imageUrl && (
              <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
            )}
          </div>

          {/* text block */}
          <div className="ml-2 flex flex-col overflow-hidden">
            {/* Option number */}
            <span className="text-left text-base md:text-sm font-extrabold text-gray-700 truncate max-w-[120px]">
              Option {index + 1}
            </span>

            {/* Clinic name */}
            <span className="text-left text-base md:text-sm font-base text-gray-800 truncate max-w-[120px]">
              {title}
            </span>
          </div>
        </button>
      </div>
    );
  };

  /* Navigation arrow icons */
  const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );

  const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  );

  const isPreviousDisabled = activeIndex === 0;
  const isNextDisabled = activeIndex === options.length - 1;

  // Button color classes based on disabled state
  const buttonColorClasses = (isDisabled) => 
    isDisabled 
      ? "text-gray-400 cursor-not-allowed" 
      : "text-gray-800 hover:text-gray-900";

  // Circle button color classes based on disabled state
  const circleColorClasses = (isDisabled) =>
    isDisabled
      ? "bg-gray-00 text-gray-400 cursor-not-allowed"
      : "bg-white text-mm_green hover:bg-mm_green hover:text-white border-mm_green";

  return (
    <div className="max-w-screen-xl pt-6 flex items-center justify-center py-1">
      {/* All elements in a single flex row with appropriate spacing */}
      <div className="flex items-center justify-center">
        {/* Previous Button - Text version for larger screens */}
        <button 
          onClick={handlePrevious}
          disabled={isPreviousDisabled}
          className={`hidden xl:flex items-center mr-8 transition-all duration-200 ${buttonColorClasses(isPreviousDisabled)}`}
          aria-label="Previous clinic"
        >
          <ChevronLeftIcon />
          <span className="ml-1 text-lg md:text-sm font-semibold">Previous clinic</span>
        </button>
        
        {/* Previous Button - Circle version for smaller screens */}
        <button
          onClick={handlePrevious}
          disabled={isPreviousDisabled}
          className={`flex xl:hidden items-center justify-center w-10 h-10 rounded-full border-2 mr-4 shadow-md transition-all duration-200 ${circleColorClasses(isPreviousDisabled)}`}
          aria-label="Previous clinic"
        >
          <ChevronLeftIcon />
        </button>
        
        {/* Mini banners */}
        <div className="flex justify-center items-center space-x-4">
          {options.map((option, idx) => (
            <MiniBanner 
              key={idx} 
              option={option} 
              isActive={idx === activeIndex} 
              index={idx}
            />
          ))}
        </div>
        
        {/* Next Button - Text version for larger screens */}
        <button 
          onClick={handleNext}
          disabled={isNextDisabled}
          className={`hidden xl:flex items-center ml-8 transition-all duration-200 ${buttonColorClasses(isNextDisabled)}`}
          aria-label="Next clinic"
        >
          <span className="mr-1 text-lg md:text-sm font-semibold">Next clinic</span>
          <ChevronRightIcon />
        </button>
        
        {/* Next Button - Circle version for smaller screens */}
        <button
          onClick={handleNext}
          disabled={isNextDisabled}
          className={`flex xl:hidden items-center justify-center w-10 h-10 rounded-full border-2 ml-4 shadow-md transition-all duration-200 ${circleColorClasses(isNextDisabled)}`}
          aria-label="Next clinic"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

export default DesktopRecTabBar;